import React, { useState } from "react";
import { Steps, Card, Collapse, Tag, Button, Divider } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";

const { Step } = Steps;
const { Panel } = Collapse;

const phaseData = [
  {
    title: "Phase 1 – Initial Setup",
    color: "blue",
    sections: [
      {
        title: "Cities → Dealers → Sales Persons",
        items: [
          "Add Cities",
          "Add Dealers (Assign City, Active Status)",
          "Add Sales Persons (Assign Dealer)",
        ],
      },
      {
        title: "Vehicle Configuration",
        items: [
          "Add Vehicle Types",
          "Add Vehicle Models",
          "Add Vehicle Colors",
          "Create Type-Model-Price Mapping (Required)",
        ],
      },
      {
        title: "Financial & Product Setup",
        items: [
          "Add Internal Bank Accounts (Active)",
          "Add Products (Accessories)",
        ],
      },
    ],
  },
  {
    title: "Phase 2 – Vehicle Management",
    color: "purple",
    sections: [
      {
        title: "Add Vehicle",
        items: [
          "Select Type-Model Mapping",
          "Select Color & Dealer",
          "Enter Engine & Chassis No",
          "Set Status: Available",
        ],
      },
      {
        title: "Stock Transfer",
        items: [
          "Select From & To Dealer",
          "Approval Required",
        ],
      },
    ],
  },
  {
    title: "Phase 3 – Customer & Inquiry",
    color: "cyan",
    sections: [
      {
        title: "Customer Inquiry",
        items: [
          "Add Customer Details",
          "Assign Dealer & Sales Person",
          "Select Vehicle Type & Model",
          "Optional Vehicle Assignment",
          "Auto Price Calculation",
        ],
      },
      {
        title: "Multiple Inquiries",
        items: ["Each Inquiry Tracked Separately"],
      },
    ],
  },
  {
    title: "Phase 4 – Payment Processing",
    color: "green",
    sections: [
      {
        title: "Bank Slip Payment",
        items: [
          "Select Customer & Inquiry",
          "Select Internal Bank Account",
          "Upload Bank Slip",
          "Auto Bank Transaction",
          "Status: Paid When Complete",
        ],
      },
      {
        title: "Partial Payments",
        items: ["Multiple Payments Allowed", "Auto Status Update"],
      },
    ],
  },
  {
    title: "Phase 5 – Product & Stock",
    color: "orange",
    sections: [
      {
        title: "Create GRN",
        items: [
          "Enter Supplier Invoice",
          "Add Product Qty & Cost",
          "Auto Stock Batch Creation",
        ],
      },
      {
        title: "View Stock",
        items: ["Real-time Stock Quantity Visibility"],
      },
    ],
  },
  {
    title: "Phase 6 – Product Offers",
    color: "magenta",
    sections: [
      {
        title: "Offer for Inquiry",
        items: [
          "Select Inquiry",
          "Select Product & Batch",
          "Validate Available Quantity",
          "Auto Stock Reservation",
        ],
      },
    ],
  },
];

export const UserGuideWorkflow = () => {
  const [current, setCurrent] = useState(0);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Sriland Bank Slip Management Workflow
          </h1>
          <p className="text-gray-500 mb-6">
            Clean operational guideline overview with dependency-driven flow.
          </p>
        </motion.div>

        <Card className="rounded-2xl shadow-md mb-6">
          <Steps
            current={current}
            onChange={(value) => setCurrent(value)}
            responsive
          >
            {phaseData.map((phase, index) => (
              <Step key={index} title={phase.title} />
            ))}
          </Steps>
        </Card>

        <motion.div
          key={current}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="rounded-2xl shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {phaseData[current].title}
              </h2>
              <Tag color={phaseData[current].color}>
                Active Phase
              </Tag>
            </div>

            <Collapse accordion bordered={false}>
              {phaseData[current].sections.map((section, idx) => (
                <Panel header={section.title} key={idx}>
                  <ul className="space-y-2">
                    {section.items.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-gray-700"
                      >
                        <CheckCircleOutlined className="text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Panel>
              ))}
            </Collapse>

            <Divider />

            <div className="flex justify-between">
              <Button
                disabled={current === 0}
                onClick={() => setCurrent(current - 1)}
              >
                Previous
              </Button>
              <Button
                type="primary"
                disabled={current === phaseData.length - 1}
                onClick={() => setCurrent(current + 1)}
              >
                Next
              </Button>
            </div>
          </Card>
        </motion.div>

        <div className="mt-8 text-sm text-gray-500">
          <p>
            ⚠ Always complete Phase 1 before proceeding. Never skip Type-Model-Price Mapping.
          </p>
          <p>✔ Validate dependencies before creating operational records.</p>
        </div>
      </div>
    </div>
  );
}
